import * as React from "react";
import {
  ArrowLeft, Bell, Plus, Trash2, Loader2, Search, ChevronRight,
  HelpCircle, ToggleLeft, Mail, History, AlertTriangle, Check, Pencil, Play, Code, X, Send
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import axios from "axios";
import { toast } from "sonner";
import { trackActivity } from "@/utils/trackActivity";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const HelpTip = ({ text }) => (
  <Tooltip delayDuration={200}>
    <TooltipTrigger asChild><HelpCircle className="h-3.5 w-3.5 text-slate-400 hover:text-slate-600 cursor-help inline-block ml-1" /></TooltipTrigger>
    <TooltipContent className="max-w-xs text-xs bg-slate-900 text-slate-100 px-3 py-2">{text}</TooltipContent>
  </Tooltip>
);

const METRICS = [
  { value: "spend_usd", label: "Spend (USD)", desc: "Gasto diario" },
  { value: "roas", label: "ROAS", desc: "Return on Ad Spend" },
  { value: "cpa", label: "CPA", desc: "Costo por conversion" },
  { value: "ctr", label: "CTR (%)", desc: "Click-through rate" },
  { value: "conversions", label: "Conversiones", desc: "Total conversiones" },
  { value: "impressions", label: "Impresiones", desc: "Total impresiones" },
  { value: "cpc", label: "CPC", desc: "Costo por clic" },
  { value: "cpm", label: "CPM", desc: "Costo por mil" },
  { value: "zero_spend", label: "Sin Gasto (Zero Spend)", desc: "Cuenta activa sin gasto ayer" },
];

const OPERATORS = [
  { value: ">", label: "Mayor que" },
  { value: "<", label: "Menor que" },
  { value: ">=", label: "Mayor o igual" },
  { value: "<=", label: "Menor o igual" },
  { value: "=", label: "Igual a" },
  { value: "change_pct_gt", label: "Aumentó % más de" },
  { value: "change_pct_lt", label: "Bajó % más de" },
];

export const PerformanceAlertsView = ({ onBack }) => {
  const [rules, setRules] = React.useState([]);
  const [triggers, setTriggers] = React.useState([]);
  const [triggersTotal, setTriggersTotal] = React.useState(0);
  const [clients, setClients] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [showCreate, setShowCreate] = React.useState(false);
  const [editingRule, setEditingRule] = React.useState(null);
  const [testResult, setTestResult] = React.useState(null);
  const [sendingPreview, setSendingPreview] = React.useState(false);

  const handleSendPreviewEmail = async () => {
    setSendingPreview(true);
    try {
      const res = await axios.post(`${API}/perf-alerts/test-consolidated-email`, {}, { withCredentials: true });
      const d = res.data || {};
      if (d.emails_sent) {
        toast.success(`Email enviado a ${d.test_recipient}`, {
          description: `${d.rules_triggered}/${d.rules_evaluated} reglas disparadas · ${d.accounts_triggered} cuentas`,
          duration: 6000,
        });
      } else {
        toast.info(d.message || "Sin disparos — no hay email que enviar", { duration: 5000 });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al enviar email de prueba");
    } finally {
      setSendingPreview(false);
    }
  };
  const [isTesting, setIsTesting] = React.useState(false);
  const [showQuery, setShowQuery] = React.useState(false);

  // Create form
  const [newName, setNewName] = React.useState("");
  const [newLevel, setNewLevel] = React.useState("account");
  const [newCliente, setNewCliente] = React.useState("all");
  const [newCampaignId, setNewCampaignId] = React.useState("");
  const [newCampaignName, setNewCampaignName] = React.useState("");
  const [newMetric, setNewMetric] = React.useState("spend_usd");
  const [newOperator, setNewOperator] = React.useState(">");
  const [newValue, setNewValue] = React.useState("");
  const [newEmail, setNewEmail] = React.useState(true);
  const [conditions, setConditions] = React.useState([{ id: "1", metric: "spend_usd", operator: ">", value: "" }]);
  const [logicalOperator, setLogicalOperator] = React.useState("AND");
  const [clientSearch, setClientSearch] = React.useState("");
  const [clientDropdownOpen, setClientDropdownOpen] = React.useState(false);
  const clientRef = React.useRef(null);
  const [campaigns, setCampaigns] = React.useState([]);
  const [campaignSearch, setCampaignSearch] = React.useState("");
  const [campaignDropdownOpen, setCampaignDropdownOpen] = React.useState(false);
  const campaignRef = React.useRef(null);
  const [loadingCampaigns, setLoadingCampaigns] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);

  // Close client dropdown on outside click
  React.useEffect(() => {
    const handler = (e) => {
      if (clientRef.current && !clientRef.current.contains(e.target)) setClientDropdownOpen(false);
      if (campaignRef.current && !campaignRef.current.contains(e.target)) setCampaignDropdownOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Sync first condition with legacy fields
  React.useEffect(() => {
    if (conditions.length > 0) {
      setNewMetric(conditions[0].metric);
      setNewOperator(conditions[0].operator);
      setNewValue(conditions[0].value);
    }
  }, [conditions]);

  const addCondition = () => {
    setConditions(prev => [...prev, { id: String(Date.now()), metric: "spend_usd", operator: ">", value: "" }]);
    setTestResult(null); setShowQuery(false);
  };
  const removeCondition = (id) => {
    setConditions(prev => prev.filter(c => c.id !== id));
    setTestResult(null); setShowQuery(false);
  };
  const updateCondition = (id, field, val) => {
    setConditions(prev => prev.map(c => {
      if (c.id !== id) return c;
      const updated = { ...c, [field]: val };
      // Auto-configure zero_spend: no operator/value needed from user
      if (field === "metric" && val === "zero_spend") {
        updated.operator = "=";
        updated.value = "1";
      }
      return updated;
    }));
    setTestResult(null); setShowQuery(false);
  };

  // Fetch campaigns when client changes and level is campaign
  React.useEffect(() => {
    if (newLevel === "campaign" && newCliente && newCliente !== "all") {
      setLoadingCampaigns(true);
      setCampaigns([]);
      setNewCampaignId("");
      setNewCampaignName("");
      axios.get(`${API}/perf-alerts/campaigns?cliente=${encodeURIComponent(newCliente)}`, { withCredentials: true })
        .then(r => setCampaigns(r.data.campaigns || []))
        .catch(() => {})
        .finally(() => setLoadingCampaigns(false));
    }
  }, [newCliente, newLevel]);

  React.useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [rulesRes, histRes, clientsRes] = await Promise.all([
        axios.get(`${API}/perf-alerts/rules`, { withCredentials: true }),
        axios.get(`${API}/perf-alerts/history?limit=30`, { withCredentials: true }),
        axios.get(`${API}/perf-alerts/clients`, { withCredentials: true }),
      ]);
      setRules(rulesRes.data.rules || []);
      setTriggers(histRes.data.triggers || []);
      setTriggersTotal(histRes.data.total || 0);
      setClients(clientsRes.data.clients || []);
      trackActivity("view_module", "performance_alerts");
    } catch (e) { console.error(e); }
    finally { setIsLoading(false); }
  };

  const handleToggle = async (ruleId, enabled) => {
    try {
      await axios.put(`${API}/perf-alerts/rules/${ruleId}`, { enabled }, { withCredentials: true });
      setRules(prev => prev.map(r => r.id === ruleId ? { ...r, enabled } : r));
    } catch (e) { toast.error("Error"); }
  };

  const handleEdit = (rule) => {
    setEditingRule(rule);
    setNewName(rule.name);
    setNewLevel(rule.level || "account");
    setNewCliente(rule.cliente || "all");
    setNewCampaignId(rule.campaign_id || "");
    setNewCampaignName(rule.campaign_name || "");
    setNewMetric(rule.metric);
    setNewOperator(rule.operator);
    setNewValue(String(rule.value));
    setNewEmail(rule.notify_email !== false);
    setLogicalOperator(rule.logical_operator || "AND");
    setConditions(rule.conditions?.length ? rule.conditions.map((c, i) => ({
      ...c,
      id: String(i),
      // For change_pct_lt the stored value is negative; show absolute in UI
      value: c.operator === "change_pct_lt" && parseFloat(c.value) < 0
               ? String(Math.abs(parseFloat(c.value)))
               : String(c.value ?? ""),
    })) : [{ id: "1", metric: rule.metric, operator: rule.operator, value: String(rule.value) }]);
    setTestResult(null);
    setShowQuery(false);
    setShowCreate(true);
  };

  const handleSave = async () => {
    const hasUnfilled = conditions.some(c => c.metric !== "zero_spend" && !c.value);
    if (hasUnfilled) { toast.error("Ingresa un valor para todas las condiciones"); return; }
    setIsSaving(true);
    try {
      const cleanConditions = conditions.map(c => ({
        metric: c.metric,
        operator: c.operator,
        value: c.metric === "zero_spend" ? 1
             : c.operator === "change_pct_lt" ? -Math.abs(parseFloat(c.value))
             : parseFloat(c.value),
      }));
      const payload = {
        name: newName || conditions.map(c => `${c.metric} ${c.operator} ${c.value}`).join(` ${logicalOperator} `),
        level: newLevel,
        cliente: newCliente === "all" ? null : newCliente,
        campaign_id: newLevel === "campaign" ? newCampaignId || null : null,
        campaign_name: newLevel === "campaign" ? newCampaignName || null : null,
        metric: conditions[0]?.metric || newMetric,
        operator: conditions[0]?.operator || newOperator,
        value: parseFloat(conditions[0]?.value || newValue),
        logical_operator: logicalOperator,
        conditions: cleanConditions,
        notify_email: newEmail,
      };
      if (editingRule) {
        await axios.put(`${API}/perf-alerts/rules/${editingRule.id}`, payload, { withCredentials: true });
        toast.success("Alerta actualizada");
      } else {
        await axios.post(`${API}/perf-alerts/rules`, payload, { withCredentials: true });
        toast.success("Alerta creada");
      }
      setShowCreate(false);
      setEditingRule(null);
      setNewName(""); setNewValue(""); setNewCliente("all"); setNewCampaignId(""); setNewCampaignName(""); setNewLevel("account");
      setConditions([{ id: "1", metric: "spend_usd", operator: ">", value: "" }]); setLogicalOperator("AND");
      setTestResult(null); setShowQuery(false);
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setIsSaving(false); }
  };

  const handleTest = async () => {
    const hasUnfilled = conditions.some(c => c.metric !== "zero_spend" && !c.value);
    if (hasUnfilled) { toast.error("Ingresa un valor para todas las condiciones"); return; }
    setIsTesting(true);
    setTestResult(null);
    try {
      const testConditions = conditions.map(c => ({
        metric: c.metric,
        operator: c.operator,
        value: c.metric === "zero_spend" ? 1
             : c.operator === "change_pct_lt" ? -Math.abs(parseFloat(c.value))
             : parseFloat(c.value),
      }));
      const res = await axios.post(`${API}/perf-alerts/test`, {
        conditions: testConditions,
        logical_operator: logicalOperator,
        metric: testConditions[0]?.metric || newMetric,
        operator: testConditions[0]?.operator || newOperator,
        value: testConditions[0]?.value ?? parseFloat(newValue),
        level: newLevel,
        cliente: newCliente === "all" ? null : newCliente,
        campaign_id: newLevel === "campaign" ? newCampaignId || null : null,
      }, { withCredentials: true });
      setTestResult(res.data);
    } catch (e) { toast.error("Error al ejecutar test"); }
    finally { setIsTesting(false); }
  };

  const handleDelete = async (ruleId) => {
    if (!confirm("¿Eliminar esta alerta?")) return;
    try {
      await axios.delete(`${API}/perf-alerts/rules/${ruleId}`, { withCredentials: true });
      toast.success("Alerta eliminada");
      loadData();
    } catch (e) { toast.error("Error"); }
  };

  if (isLoading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  const activeCount = rules.filter(r => r.enabled).length;


  // Form validation
  const isFormValid = conditions.every(c => c.value && c.metric) && (newLevel !== "campaign" || (newCliente && newCliente !== "all"));
  const getMetricPrefix = (m) => ["spend_usd","cpa","cpc","cpm"].includes(m) ? "$" : "";
  const getMetricSuffix = (m) => ["ctr"].includes(m) ? "%" : ["roas"].includes(m) ? "x" : "";

  // === CREATE/EDIT VIEW ===
  if (showCreate) {
    return (
      <TooltipProvider>
      <div className="min-h-screen bg-[#F8FAFC]">
        <header className="border-b bg-white sticky top-0 z-10 shadow-sm">
          <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => { setShowCreate(false); setEditingRule(null); setTestResult(null); setShowQuery(false); }} className="text-slate-500 hover:text-slate-800">
                <ArrowLeft className="h-4 w-4 mr-1" />Volver
              </Button>
              <div className="h-5 w-px bg-slate-200" />
              <h1 className="text-base font-semibold text-slate-800">{editingRule ? "Editar Alerta" : "Configurar Nueva Alerta"}</h1>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => { setShowCreate(false); setEditingRule(null); }}>Cancelar</Button>
              <Button size="sm" className="bg-[#E67E22] hover:bg-[#D35400] text-white shadow-sm" onClick={handleSave} disabled={isSaving || !isFormValid}>
                {isSaving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Check className="h-4 w-4 mr-1.5" />}
                {editingRule ? "Guardar Cambios" : "Crear Alerta"}
              </Button>
            </div>
          </div>
        </header>
        <main className="max-w-3xl mx-auto px-6 py-8 space-y-6">
          {/* A. Alcance */}
          <section className="bg-white rounded-xl border shadow-sm p-6 space-y-5">
            <div><h2 className="text-sm font-semibold text-slate-800 mb-0.5">Alcance</h2><p className="text-xs text-slate-400">Define el nombre, nivel y objetivo de esta alerta</p></div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-600">Nombre de la alerta</Label>
              <Input placeholder="Ej: Gasto excesivo en Meta" value={newName} onChange={(e) => setNewName(e.target.value)} className="rounded-lg" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-600">Nivel de monitoreo</Label>
              <div className="flex bg-slate-100 rounded-lg p-1 gap-1">
                <button className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${newLevel === "account" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                  onClick={() => { setNewLevel("account"); setNewCampaignId(""); setNewCampaignName(""); }}>Cuenta</button>
                <button className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${newLevel === "campaign" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                  onClick={() => setNewLevel("campaign")}>Campaña</button>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-600">Cliente {newLevel === "campaign" && <span className="text-[#E67E22]">*</span>}</Label>
              <div className="relative" ref={clientRef}>
                <div className="flex items-center border rounded-lg px-3 py-2.5 cursor-pointer hover:border-slate-400 bg-white" onClick={() => setClientDropdownOpen(!clientDropdownOpen)}>
                  <Search className="h-4 w-4 text-slate-300 mr-2" />
                  <span className={`text-sm flex-1 ${newCliente === "all" ? "text-slate-400" : "text-slate-800"}`}>{newCliente === "all" ? "Todos los clientes" : newCliente}</span>
                  <ChevronRight className={`h-4 w-4 text-slate-300 transition-transform ${clientDropdownOpen ? "rotate-90" : ""}`} />
                </div>
                {clientDropdownOpen && (
                  <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border rounded-xl shadow-lg max-h-[260px] flex flex-col overflow-hidden">
                    <div className="p-2.5 border-b"><Input placeholder="Buscar cliente..." value={clientSearch} onChange={(e) => setClientSearch(e.target.value)} className="h-9 text-sm rounded-lg" autoFocus /></div>
                    <div className="overflow-y-auto flex-1">
                      <div className={`px-3 py-2.5 text-sm cursor-pointer hover:bg-slate-50 ${newCliente === "all" ? "bg-orange-50 text-[#E67E22] font-medium" : ""}`} onClick={() => { setNewCliente("all"); setClientDropdownOpen(false); setClientSearch(""); }}>Todos los clientes</div>
                      {clients.filter(c => !clientSearch || c.toLowerCase().includes(clientSearch.toLowerCase())).map(c => (
                        <div key={c} className={`px-3 py-2.5 text-sm cursor-pointer hover:bg-slate-50 ${newCliente === c ? "bg-orange-50 text-[#E67E22] font-medium" : ""}`} onClick={() => { setNewCliente(c); setClientDropdownOpen(false); setClientSearch(""); }}>{c}</div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            {newLevel === "campaign" && newCliente && newCliente !== "all" && (
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-600">Campaña {loadingCampaigns && <Loader2 className="h-3 w-3 inline animate-spin ml-1" />}</Label>
                <div className="relative" ref={campaignRef}>
                  <div className="flex items-center border rounded-lg px-3 py-2.5 cursor-pointer hover:border-slate-400 bg-white" onClick={() => !loadingCampaigns && setCampaignDropdownOpen(!campaignDropdownOpen)}>
                    <Search className="h-4 w-4 text-slate-300 mr-2" />
                    <span className={`text-sm flex-1 truncate ${!newCampaignId ? "text-slate-400" : "text-slate-800"}`}>{newCampaignId ? newCampaignName : "Todas las campañas"}</span>
                    <ChevronRight className={`h-4 w-4 text-slate-300 transition-transform ${campaignDropdownOpen ? "rotate-90" : ""}`} />
                  </div>
                  {campaignDropdownOpen && (
                    <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border rounded-xl shadow-lg max-h-[220px] flex flex-col overflow-hidden">
                      <div className="p-2.5 border-b"><Input placeholder="Buscar campaña..." value={campaignSearch} onChange={(e) => setCampaignSearch(e.target.value)} className="h-9 text-sm rounded-lg" autoFocus /></div>
                      <div className="overflow-y-auto flex-1">
                        <div className={`px-3 py-2.5 text-sm cursor-pointer hover:bg-slate-50 ${!newCampaignId ? "bg-orange-50 text-[#E67E22] font-medium" : ""}`} onClick={() => { setNewCampaignId(""); setNewCampaignName(""); setCampaignDropdownOpen(false); }}>Todas las campañas</div>
                        {campaigns.filter(c => !campaignSearch || (c.campaign_name || "").toLowerCase().includes(campaignSearch.toLowerCase())).map(c => (
                          <div key={c.campaign_id} className={`px-3 py-2 cursor-pointer hover:bg-slate-50 ${newCampaignId === c.campaign_id ? "bg-orange-50 text-[#E67E22]" : ""}`}
                            onClick={() => { setNewCampaignId(c.campaign_id); setNewCampaignName(c.campaign_name); setCampaignDropdownOpen(false); }}>
                            <p className="text-sm truncate">{c.campaign_name}</p><Badge variant="outline" className="text-[10px] mt-0.5">{c.platform}</Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            {newLevel === "campaign" && (!newCliente || newCliente === "all") && <p className="text-xs text-[#E67E22]">Selecciona un cliente primero para ver sus campañas</p>}
          </section>

          {/* B. Condicion */}
          <section className="bg-white rounded-xl border shadow-sm p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div><h2 className="text-sm font-semibold text-slate-800 mb-0.5">Condiciones</h2><p className="text-xs text-slate-400">Define cuando se debe disparar la alerta</p></div>
              {conditions.length > 1 && (
                <div className="flex bg-slate-100 rounded-lg p-0.5 gap-0.5">
                  <button className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${logicalOperator === "AND" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500"}`}
                    onClick={() => { setLogicalOperator("AND"); setTestResult(null); setShowQuery(false); }}>AND (todas)</button>
                  <button className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${logicalOperator === "OR" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500"}`}
                    onClick={() => { setLogicalOperator("OR"); setTestResult(null); setShowQuery(false); }}>OR (alguna)</button>
                </div>
              )}
            </div>
            <div className="space-y-2">
              {conditions.map((cond, idx) => (
                <div key={cond.id} className="bg-slate-50 rounded-lg p-4">
                  {idx === 0 && <p className="text-sm text-slate-600 mb-3">Notificarme cuando...</p>}
                  {idx > 0 && (
                    <div className="flex items-center gap-2 mb-3">
                      <div className="h-px flex-1 bg-slate-200" />
                      <Badge className={`text-[10px] ${logicalOperator === "AND" ? "bg-indigo-100 text-indigo-700" : "bg-amber-100 text-amber-700"}`}>{logicalOperator}</Badge>
                      <div className="h-px flex-1 bg-slate-200" />
                    </div>
                  )}
                  <div className="flex items-center gap-2 flex-wrap">
                    <Select value={cond.metric} onValueChange={(v) => updateCondition(cond.id, "metric", v)}>
                      <SelectTrigger className="w-[180px] h-10 rounded-lg bg-white text-sm font-medium"><SelectValue /></SelectTrigger>
                      <SelectContent>{METRICS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
                    </Select>
                    {cond.metric === "zero_spend" ? (
                      <div className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
                        <span className="text-xs font-medium text-red-700">detectado</span>
                        <span className="text-[10px] text-red-500">— cuenta activa sin gasto ayer</span>
                      </div>
                    ) : (
                      <>
                        <Select value={cond.operator} onValueChange={(v) => updateCondition(cond.id, "operator", v)}>
                          <SelectTrigger className="w-[170px] h-10 rounded-lg bg-white text-sm"><SelectValue /></SelectTrigger>
                          <SelectContent>{OPERATORS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                        </Select>
                        <div className="relative">
                          {(cond.operator !== "change_pct_gt" && cond.operator !== "change_pct_lt") && getMetricPrefix(cond.metric) &&
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">{getMetricPrefix(cond.metric)}</span>}
                          <Input type="number" min="0"
                            placeholder={cond.operator === "change_pct_gt" ? "30" : cond.operator === "change_pct_lt" ? "20" : "0"}
                            value={cond.value} onChange={(e) => updateCondition(cond.id, "value", e.target.value)}
                            className={`w-[120px] h-10 rounded-lg text-sm font-medium ${(cond.operator !== "change_pct_gt" && cond.operator !== "change_pct_lt") && getMetricPrefix(cond.metric) ? "pl-7" : ""}`} />
                          {(cond.operator === "change_pct_gt" || cond.operator === "change_pct_lt")
                            ? <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">%</span>
                            : getMetricSuffix(cond.metric) && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">{getMetricSuffix(cond.metric)}</span>}
                        </div>
                      </>
                    )}
                    {conditions.length > 1 && (
                      <Button variant="ghost" size="sm" className="h-10 w-10 p-0 text-slate-400 hover:text-red-500" onClick={() => removeCondition(cond.id)}>
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <Button variant="outline" size="sm" className="w-full rounded-lg border-dashed text-xs text-slate-500 hover:text-slate-700" onClick={addCondition}>
              <Plus className="h-3.5 w-3.5 mr-1.5" />Agregar condicion
            </Button>
            {conditions.some(c => c.value || c.metric === "zero_spend") && (
              <div className="text-xs text-slate-500 bg-slate-50 rounded-lg px-3 py-2 border border-dashed">
                Resumen: Alertar si {conditions.filter(c => c.value || c.metric === "zero_spend").map((c, i) => (
                  <span key={c.id}>{i > 0 && <strong className={logicalOperator === "AND" ? "text-indigo-600" : "text-amber-600"}> {logicalOperator} </strong>}
                    {c.metric === "zero_spend"
                      ? <><strong>Sin Gasto</strong> detectado (cuenta activa sin gasto ayer)</>
                      : c.operator === "change_pct_gt"
                        ? <><strong>{METRICS.find(m=>m.value===c.metric)?.label}</strong> aumentó más de <strong>{c.value}%</strong></>
                        : c.operator === "change_pct_lt"
                          ? <><strong>{METRICS.find(m=>m.value===c.metric)?.label}</strong> bajó más de <strong>{c.value}%</strong></>
                          : <><strong>{METRICS.find(m=>m.value===c.metric)?.label}</strong> {OPERATORS.find(o=>o.value===c.operator)?.label?.toLowerCase()} <strong>{getMetricPrefix(c.metric)}{c.value}{getMetricSuffix(c.metric)}</strong></>
                    }
                  </span>
                ))}{newCliente !== "all" ? ` para ${newCliente}` : ""}
              </div>
            )}
          </section>

          {/* C. Notificacion */}
          <section className="bg-white rounded-xl border shadow-sm p-6 space-y-4">
            <div><h2 className="text-sm font-semibold text-slate-800 mb-0.5">Notificacion</h2><p className="text-xs text-slate-400">Frecuencia y canales</p></div>
            <div className="flex items-center gap-3 p-3 rounded-lg bg-amber-50 border border-amber-100"><Bell className="h-4 w-4 text-[#E67E22]" /><p className="text-xs text-slate-600">Evaluacion automatica <strong className="text-slate-800">diaria a las 7:00 AM</strong> (Panama) via DataPulse</p></div>
            <div className="flex items-center justify-between p-3 rounded-lg border"><div className="flex items-center gap-2"><Mail className="h-4 w-4 text-slate-400" /><span className="text-sm text-slate-700">Enviar notificacion por email</span></div><Switch checked={newEmail} onCheckedChange={setNewEmail} /></div>
          </section>

          {/* D. Test */}
          <section className="bg-slate-50 rounded-xl border border-dashed p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div><h2 className="text-sm font-semibold text-slate-800 mb-0.5">Probar antes de crear</h2><p className="text-xs text-slate-400">Verifica con datos reales de los ultimos 7 dias</p></div>
              <Button variant="outline" size="sm" className="rounded-lg text-xs" onClick={handleTest}
                disabled={isTesting || conditions.some(c => c.metric !== "zero_spend" && !c.value)}>
                {isTesting ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Play className="h-3.5 w-3.5 mr-1.5" />}Ejecutar Test
              </Button>
            </div>
            {!testResult && !isTesting && <div className="flex items-center justify-center py-6"><p className="text-xs text-slate-400">Completa la condicion y ejecuta el test</p></div>}
            {testResult && (
              <div className={`rounded-lg border p-4 ${testResult.total_matches > 0 ? "bg-amber-50 border-amber-200" : "bg-emerald-50 border-emerald-200"}`}>
                <div className="flex items-start gap-3 mb-3">
                  {testResult.total_matches > 0 ? <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5" /> : <Check className="h-5 w-5 text-emerald-500 mt-0.5" />}
                  <div>
                    <p className={`text-sm font-semibold ${testResult.total_matches > 0 ? "text-amber-800" : "text-emerald-800"}`}>
                      {testResult.total_matches > 0 ? `Se habria disparado ${testResult.total_matches} veces en la ultima semana` : "No se habria disparado en la ultima semana"}
                    </p>
                    <p className="text-[11px] text-slate-500 mt-0.5">{testResult.description}</p>
                  </div>
                </div>
                {testResult.total_matches > 0 && testResult.sample?.length > 0 && (
                  <div className="max-h-[200px] overflow-auto rounded-lg border bg-white mt-2">
                    {(() => {
                      // Deduplicate metrics by name
                      const seen = new Set();
                      const uniqueConds = conditions.filter(c => { if (!c.value || seen.has(c.metric)) return false; seen.add(c.metric); return true; });
                      return (
                    <table className="w-full text-[11px]"><thead><tr className="bg-slate-50 sticky top-0"><th className="px-3 py-2 text-left font-medium text-slate-500">Cliente</th><th className="px-3 py-2 text-left font-medium text-slate-500">{newLevel === "campaign" ? "Campaña" : "Cuenta"}</th><th className="px-3 py-2 text-left font-medium text-slate-500">Fecha</th>{uniqueConds.map((c,i) => <th key={i} className="px-3 py-2 text-right font-medium text-slate-500">{c.metric === "zero_spend" ? "Estado" : (c.operator === "change_pct_gt" || c.operator === "change_pct_lt") ? `${METRICS.find(m=>m.value===c.metric)?.label} Δ%` : (METRICS.find(m=>m.value===c.metric)?.label || c.metric)}</th>)}</tr></thead>
                    <tbody>{testResult.sample.map((r, i) => (<tr key={i} className="border-t"><td className="px-3 py-1.5 text-slate-700">{r.cliente}</td><td className="px-3 py-1.5 text-slate-600 truncate max-w-[200px]">{newLevel === "campaign" ? r.campaign_name : r.account_name}</td><td className="px-3 py-1.5 text-slate-500">{r.date_day}</td>{uniqueConds.map((c,j) => { const val = r[`val_${c.metric}`]; return (<td key={j} className="px-3 py-1.5 text-right">{c.metric === "zero_spend" ? <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-[10px] font-medium">Sin gasto</span> : (c.operator === "change_pct_gt" || c.operator === "change_pct_lt") ? <span className={`font-mono font-semibold ${val >= 0 ? "text-emerald-600" : "text-red-600"}`}>{val >= 0 ? "+" : ""}{val != null ? val : "-"}%</span> : <span className="font-mono font-semibold">{getMetricPrefix(c.metric)}{val != null ? val : "-"}{getMetricSuffix(c.metric)}</span>}</td>); })}</tr>))}</tbody></table>
                      );
                    })()}
                  </div>
                )}
                {testResult.total_matches === 0 && (<div className="mt-2 text-xs text-slate-500"><p>Posibles ajustes:</p><ul className="list-disc ml-4 space-y-0.5 text-slate-400"><li>Reducir el umbral ({getMetricPrefix(newMetric)}{newValue}{getMetricSuffix(newMetric)})</li><li>Cambiar el operador</li>{newLevel === "campaign" && <li>Probar a nivel cuenta</li>}</ul></div>)}
              </div>
            )}
            {testResult?.query_preview && <button onClick={() => setShowQuery(!showQuery)} className="text-[11px] text-slate-400 hover:text-slate-600 underline decoration-dashed underline-offset-2">{showQuery ? "Ocultar consulta (SQL)" : "Ver consulta tecnica (SQL)"}</button>}
            {showQuery && testResult?.query_preview && <pre className="text-[10px] bg-slate-900 text-emerald-400 p-4 rounded-lg whitespace-pre-wrap break-words max-h-[180px] overflow-y-auto font-mono leading-relaxed">{testResult.query_preview}</pre>}
          </section>
        </main>
      </div>
      </TooltipProvider>
    );
  }

  // === LIST VIEW ===

  return (
    <TooltipProvider>
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-amber-50/20">
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={onBack}><ArrowLeft className="h-4 w-4 mr-2" />Volver</Button>
              <div className="h-6 w-px bg-slate-200" />
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-gradient-to-br from-amber-500 to-orange-500">
                  <Bell className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h1 className="text-lg font-bold text-slate-800 inline-flex items-center gap-1">
                    Performance Alerts
                    <HelpTip text="Crea reglas que monitorean metricas de cuentas publicitarias. Cuando una condicion se cumple, recibes notificacion por email y en la plataforma." />
                  </h1>
                  <p className="text-xs text-slate-500">{activeCount} alertas activas de {rules.length}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Tooltip delayDuration={200}>
                <TooltipTrigger asChild>
                  <Button
                    data-testid="send-preview-email-btn"
                    variant="outline"
                    onClick={handleSendPreviewEmail}
                    disabled={sendingPreview || rules.length === 0}
                    className="border-amber-200 text-amber-700 hover:bg-amber-50"
                  >
                    {sendingPreview ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                    Enviar prueba ahora
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs text-xs bg-slate-900 text-slate-100 px-3 py-2">
                  Ejecuta ahora la evaluacion de todas tus reglas y te envia el email consolidado (solo a tu email, no a los destinatarios reales).
                </TooltipContent>
              </Tooltip>
              <Button onClick={() => { setEditingRule(null); setNewName(""); setNewValue(""); setNewCliente("all"); setNewLevel("account"); setNewCampaignId(""); setNewCampaignName(""); setNewMetric("spend_usd"); setNewOperator(">"); setNewEmail(true); setConditions([{id:"1",metric:"spend_usd",operator:">",value:""}]); setLogicalOperator("AND"); setTestResult(null); setShowQuery(false); setShowCreate(true); }} className="bg-amber-600 hover:bg-amber-700">
                <Plus className="h-4 w-4 mr-2" />Nueva Alerta
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        <Tabs defaultValue="rules">
          <TabsList>
            <TabsTrigger value="rules" className="flex items-center gap-1.5"><Bell className="h-3.5 w-3.5" />Alertas<Badge variant="outline" className="text-[10px] ml-1">{rules.length}</Badge></TabsTrigger>
            <TabsTrigger value="history" className="flex items-center gap-1.5"><History className="h-3.5 w-3.5" />Historial<Badge variant="outline" className="text-[10px] ml-1">{triggersTotal}</Badge></TabsTrigger>
          </TabsList>

          <TabsContent value="rules" className="space-y-4 mt-4">
            {rules.length === 0 ? (
              <Card><CardContent className="p-8 text-center">
                <Bell className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No tienes alertas configuradas</p>
                <p className="text-xs text-slate-400 mt-1">Crea tu primera alerta para monitorear metricas automaticamente</p>
                <Button className="mt-4 bg-amber-600 hover:bg-amber-700" onClick={() => setShowCreate(true)}><Plus className="h-4 w-4 mr-1" />Crear Alerta</Button>
              </CardContent></Card>
            ) : (
              rules.map(r => (
                <Card key={r.id} className={`transition-all ${!r.enabled ? "opacity-50" : ""}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className={`p-2 rounded-lg ${r.enabled ? "bg-amber-100" : "bg-slate-100"}`}>
                          <Bell className={`h-4 w-4 ${r.enabled ? "text-amber-600" : "text-slate-400"}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold text-sm text-slate-800 truncate">{r.name}</h3>
                            {r.notify_email && <Badge className="text-[9px] bg-blue-50 text-blue-600"><Mail className="h-2.5 w-2.5 mr-0.5" />Email</Badge>}
                          </div>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            {(r.conditions?.length > 1 ? r.conditions : [r]).map((c, i) => (
                              <span key={i} className="inline-flex items-center gap-1">
                                {i > 0 && <Badge className={`text-[9px] ${(r.logical_operator||"AND")==="AND" ? "bg-indigo-50 text-indigo-600" : "bg-amber-50 text-amber-600"}`}>{r.logical_operator||"AND"}</Badge>}
                                <code className="text-xs bg-slate-100 px-2 py-0.5 rounded text-slate-700">{c.metric} {c.operator} {c.value}</code>
                              </span>
                            ))}
                            {r.cliente && <Badge variant="outline" className="text-[10px]">{r.cliente}</Badge>}
                            {!r.cliente && <Badge variant="outline" className="text-[10px] text-slate-400">Todos</Badge>}
                            <Badge variant="outline" className="text-[10px]">{(r.level || "account") === "campaign" ? "Campaña" : "Cuenta"}</Badge>
                            {r.campaign_name && <Badge variant="outline" className="text-[10px] text-indigo-600">{r.campaign_name}</Badge>}
                            {r.trigger_count > 0 && (
                              <span className="text-[10px] text-amber-600">{r.trigger_count}x disparada</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-indigo-600" onClick={() => handleEdit(r)} title="Editar">
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Switch checked={r.enabled} onCheckedChange={(v) => handleToggle(r.id, v)} className="scale-75" />
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-red-600" onClick={() => handleDelete(r.id)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          <TabsContent value="history" className="mt-4">
            {triggers.length === 0 ? (
              <Card><CardContent className="p-8 text-center">
                <History className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No hay disparos registrados</p>
              </CardContent></Card>
            ) : (
              <div className="rounded-lg border overflow-hidden">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead className="w-[25%]">Alerta</TableHead>
                    <TableHead className="w-[20%]">Condicion</TableHead>
                    <TableHead className="w-[15%] text-center">Cuentas</TableHead>
                    <TableHead className="w-[20%]">Fecha</TableHead>
                    <TableHead className="w-[20%]">Top Afectadas</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {triggers.map((t, i) => {
                      const isExecution = t.type === "execution" || t.rule_id === "__job_execution__";
                      return (
                      <TableRow key={i} className={`text-xs ${isExecution ? "bg-slate-50" : ""}`}>
                        <TableCell className="font-medium">
                          {isExecution ? (
                            <div>
                              <span className="text-indigo-600 font-semibold">Ejecucion del Job</span>
                              <p className="text-[10px] text-slate-400 mt-0.5">{t.rules_evaluated || 0} reglas evaluadas</p>
                            </div>
                          ) : t.rule_name}
                        </TableCell>
                        <TableCell>
                          {isExecution ? (
                            <div className="space-y-0.5">
                              {t.rules_detail?.map((d, j) => (
                                <div key={j} className="flex items-center gap-1">
                                  <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${d.status === "triggered" ? "bg-amber-500" : "bg-emerald-400"}`} />
                                  <span className="truncate">{d.rule_name}</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <code className="bg-slate-100 px-1.5 py-0.5 rounded">{t.metric} {t.operator} {t.threshold}</code>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {isExecution ? (
                            <div>
                              <Badge className={t.rules_triggered > 0 ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}>
                                {t.rules_triggered || 0} / {t.rules_evaluated || 0}
                              </Badge>
                              <p className="text-[10px] text-slate-400 mt-0.5">{t.accounts_analyzed || 0} ctas</p>
                            </div>
                          ) : (
                            <Badge className="bg-amber-100 text-amber-700">{t.total_accounts_triggered}</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-slate-500 whitespace-nowrap">
                          {t.triggered_at ? new Date(t.triggered_at).toLocaleString("es-ES", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "-"}
                        </TableCell>
                        <TableCell className="text-[11px] text-slate-500 truncate max-w-0">
                          {isExecution ? (
                            t.rules_triggered > 0
                              ? t.rules_detail?.filter(d => d.status === "triggered").map(d => `${d.rule_name} (${d.accounts_triggered})`).join(", ")
                              : "Sin disparos"
                          ) : (
                            <>
                              {t.triggered_accounts?.slice(0, 3).map(a => a.account_name).join(", ")}
                              {t.triggered_accounts?.length > 3 && "..."}
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

    </div>
    </TooltipProvider>
  );
};
